import {useMutation, useQuery} from '@apollo/client'
import React, {useEffect, useMemo, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {CREATE_PIPELINE, UPDATE_PIPELINE} from '../../../gql/mutations/pipelineMutations'
import {IResAddPipe, PipelineMutationPayload} from '../../../types/pipelines'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'
import {GET_ALL_ORGANIZATIONS} from '../../../gql/queries/organizationsQuery'
import {useGetUser} from '../../../store/userStore'

interface IModalAddPipelines {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoEdit?: null | {id: string; name: string; organization?: {id: string; name: string}}
}

export const ModalAddPipeline: React.FC<IModalAddPipelines> = ({
  isOpen,
  onClose,
  refetch,
  infoEdit,
}) => {
  const mutationDoc = infoEdit ? UPDATE_PIPELINE : CREATE_PIPELINE
  const [gqlAddEditPipeline, {data, loading, error}] = useMutation<IResAddPipe>(mutationDoc)
  const [selectedOrgId, setSelectedOrgId] = useState<string>('')

  // Get current user info
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUser = useGetUser((s) => s.user?.user)
  const isSuperAdministrador = userGroups.includes('SuperAdministrador')

  // Normalize ID helper
  const normalizeId = (id?: string | number | null) => (id ? `${parseId(id)}` : '')

  // Query for organizations
  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (error) toast.error('Error: ' + error.message)
  }, [error])

  // Sync default org selection when modal opens or data changes
  useEffect(() => {
    // Prefer the organization from the pipeline being edited
    if (infoEdit?.organization?.id) {
      setSelectedOrgId(normalizeId(infoEdit.organization.id))
      return
    }

    // For non superadmin, force current user's org
    if (!isSuperAdministrador && currentUser?.organization?.id) {
      setSelectedOrgId(normalizeId(currentUser.organization.id))
      return
    }

    // Otherwise empty
    setSelectedOrgId('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoEdit, isSuperAdministrador, currentUser?.organization?.id, isOpen])

  useEffect(() => {
    if (!data) return
    const payload: PipelineMutationPayload | undefined =
      data.updatePipelineMutation || data.createPipelineMutation

    if (payload?.pipeline?.id) {
      toast.success(`Success: pipeline ${infoEdit ? 'edited correctly' : 'successfully added'}`)
      onClose()
      refetch()
    } else if (payload?.errors?.length) {
      toast.error(
        `Error: ${
          payload.errors?.[0]?.message ||
          payload.errors?.[0]?.messages?.[0] ||
          'something happened'
        }`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const {name} = Object.fromEntries(new FormData(e.currentTarget))
    const orgIdValue = selectedOrgId ? parseId(selectedOrgId) : undefined

    if (infoEdit) {
      gqlAddEditPipeline({
        variables: {
          pipelineId: parseId(infoEdit.id),
          pipelineData: {
            name,
            organizationId: orgIdValue,
          },
        },
      })
    } else {
      gqlAddEditPipeline({
        variables: {
          pipelineData: {
            name,
            organizationId: orgIdValue,
          },
        },
      })
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{infoEdit ? 'Edit' : 'Add'} Pipeline</Modal.Title>
      </Modal.Header>
      <Form onSubmit={onSubmit}>
        <Modal.Body>
          <Form.Group className='mb-3' controlId='exampleForm.ControlInput1'>
            <Form.Label>Pipeline name</Form.Label>
            <Form.Control
              type='name'
              name='name'
              defaultValue={infoEdit ? infoEdit.name : ''}
              required
              placeholder='Enter name'
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Organization</Form.Label>
            <Form.Select
              name='organizationId'
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              disabled={!isSuperAdministrador || loadingOrganizations}
            >
              {loadingOrganizations ? (
                <option value=''>Loading...</option>
              ) : (
                <option value=''>Select One</option>
              )}
              {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => {
                const value = normalizeId(node.id)
                return (
                  <option key={node.id} value={value}>
                    {node.name}
                  </option>
                )
              })}
            </Form.Select>
            {!isSuperAdministrador && (
              <Form.Text className='text-muted'>
                Organization is set to your current organization. Only SuperAdministrador can change this.
              </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button
            type='submit'
            variant='primary'
            className='d-flex align-items-center'
            disabled={loading}
          >
            {loading && <Spinner animation='border' size='sm' />}
            Save Changes
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
